<template>
  <div class="report-inspector-scene">
    <div v-if="sceneState.error" class="error">
      {{ $t('An error has occured while receiving information') }}
    </div>
    <el-tooltip ref="toolTipElement"
                v-model="show_tooltip"
                v-click-outside="handleClickOutside"
                :popper-options="popperOptions"
                placement="bottom"
                :manual="true"
                popper-class="report-inspector-scene__tooltip"
                effect="light">
      <span :style="tooltip.style" />
      <div slot="content"
           class="report-inspector-scene__tooltip-content"
           :class="{ 'is-unrecognized': isUnrecognized, 'is-meta': isMeta}">
        <div v-if="crop"
             :key="crop.id"
             v-loading="tooltipIsLoading"
             class="report-inspector">
          <span v-if="cropState"
                class="crop-legend-state"
                :style="`color:${cropState.color}`">
            {{ $t(normalizeString(cropState.label)) }}
          </span>
          <el-row v-if="!isNonSku"
                  :gutter="10"
                  class="report-inspector__content">
            <el-col :span="6" class="image-wrapper">
              <div class="report-inspector__content-image" :style="imageStyle" />
            </el-col>
            <el-col :span="18" class="crop-detail">
              <el-row>
                <el-col class="detail" :span="10">
                  <b> {{ $t('Name') }}:</b>
                </el-col>
                <el-col class="detail" :span="14">
                  {{ isUnrecognized ? $t('Unresolved') : crop.display_name }}
                </el-col>
              </el-row>

              <el-row>
                <el-col class="detail" :span="10">
                  <b> {{ $t('Brand') }}:</b>
                </el-col>
                <el-col class="detail" :span="14">
                  {{ isUnrecognized ? $t('Unresolved') : crop.brand }}
                </el-col>
              </el-row>

              <el-row>
                <el-col class="detail" :span="10">
                  <b> {{ $t('Producer') }}:</b>
                </el-col>
                <el-col class="detail" :span="14">
                  {{ isUnrecognized ? $t('Unresolved') : crop.producer }}
                </el-col>
              </el-row>

              <el-row>
                <el-col class="detail" :span="10">
                  <b> {{ $t('Customer ID') }}:</b>
                </el-col>
                <el-col class="detail" :span="14">
                  {{ isUnrecognized ? $t('Unresolved') : crop.customer_id }}
                </el-col>
              </el-row>

              <el-row>
                <el-col class="detail" :span="10">
                  <b> {{ $t('Size') }}:</b>
                </el-col>
                <el-col class="detail" :span="14">
                  <template v-if="isUnrecognized">
                    {{ $t('Unresolved') }}
                  </template>
                  <template v-else>
                    {{ crop.size }} {{ crop.size_unit }}
                  </template>
                </el-col>
              </el-row>
            </el-col>
          </el-row>

          <div v-else-if="crop.crop.text"
               class="report-inspector__content"
               style="justify-content: center;">
            <span v-if="crop.crop.text" style="font-size: 50px; line-height: 2.2;">{{ crop.crop.text }}</span>
          </div>

          <el-row v-else
                  :gutter="10"
                  class="report-inspector__content">
            <el-col :span="6" class="image-wrapper">
              <div class="report-inspector__content-image" :style="imageStyle" />
            </el-col>
            <el-col :span="18" class="crop-detail">
              <el-row>
                <el-col class="detail" :span="12">
                  <b>{{ $t('Name') }}:</b>
                </el-col>
                <el-col class="detail" :span="12">
                  {{ crop.display_name }}
                </el-col>
              </el-row>

              <el-row>
                <el-col class="detail" :span="12">
                  <b>{{ $t('Description') }}:</b>
                </el-col>
                <el-col class="detail" :span="12">
                  {{ crop.description }}
                </el-col>
              </el-row>

              <el-row>
                <el-col class="detail" :span="12">
                  <b style="word-break: keep-all;">{{ $t('Non Sku Type') }}:</b>
                </el-col>
                <el-col class="detail" :span="12">
                  {{ crop.non_sku_type }}
                </el-col>
              </el-row>
            </el-col>
          </el-row>
        </div>
      </div>
    </el-tooltip>
    <span v-if="fitToScreenButtonVisibility"
          class="mdi mdi-fit-to-screen fit-to-screen-button"
          @click="handleFitToScreen" />
  </div>
</template>

<script>
import { mapState } from 'vuex';
import { result } from 'lodash';
import MobileScreenControl from '@/helpers/mixins/mobileScreenControl';
import ReportInspectorScene from '../report-inspector-scene';
import bus, { EVENTS } from '../report-inspector-scene/bus';

export default {
  name: 'Scene',
  mixins: [MobileScreenControl],
  props: {
    mosaic: {
      type: String,
      default: ''
    },
    imageSets: {
      type: Array,
      required: true
    },
    reportDataImages: {
      type: Array,
      required: true
    },
    imageResults: {
      type: Array,
      default() { return []; }
    },
    report: {
      type: Object,
      default() { return {}; }
    },
    bgAlpha: {
      type: Number,
      default: 0.75
    },
    skus: {
      type: Array,
      default() { return []; }
    },
    reference: {
      type: Boolean,
      default: false
    },
    sceneZoomScaleValue: {
      type: Number,
      default: 0.4
    },
    isFullScreen: {
      type: Boolean,
      default: false
    },
    isFilledColors: {
      type: Boolean,
      default: false
    },
    recognitionResults: {
      type: Boolean,
      default: false
    },
    imageSetId: {
      type: [String, Number],
      default: ''
    },
    referenceImageSetId: {
      type: [String, Number],
      default: ''
    },
    nonSkuList: {
      type: Array,
      default() { return []; }
    },
    isSkuListActive: {
      type: Boolean,
      default: false
    },
    selectedImageNumber: {
      type: Number,
      default: 1
    },
    sceneTitle: {
      type: String,
      default: ''
    },
    isReversed: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      bus,
      sceneState: {
        loading: false,
        error: false
      },
      crop: null,
      show_tooltip: false,
      popperOptions: {},
      tooltipIsLoading: false,
      timeout: null,
      sceneHeightGap: 304,
      cropState: null
    };
  },
  computed: {
    ...mapState(['Project']),
    isNonSku() {
      return result(this.crop, 'crop.is_nonSku') === true;
    },
    isMeta() {
      return result(this.crop, 'crop.is_meta');
    },
    isSku() {
      return result(this.crop, 'crop.is_sku');
    },
    isCompetitor() {
      return result(this.crop, 'crop.is_competitor');
    },
    isUnrecognized() {
      return result(this.crop, 'crop.is_unrecognized');
    },
    tooltip() {
      if (!this.crop) return {};

      const {
        x, y, width, height
      } = this.crop.bounds;

      return {
        style: {
          position: 'absolute',
          cursor: 'pointer',
          zIndex: -2,
          width: `${width}px`,
          height: `${height}px`,
          left: `${x}px`,
          top: `${y}px`
        }
      };
    },
    imageStyle() {
      if (!this.crop) return {};

      if (this.isNonSku) return { backgroundImage: `url(${this.crop.image})` };

      return {
        backgroundImage: this.crop.images ? `url(${this.crop.images})` : 'url("/statics/undefined.svg")'
      };
    },
    fitToScreenButtonVisibility() {
      return bus.sceneZoomScaleValue.realogram > bus.realogramSceneFitScaleValue && bus.selectedMisplacedSkuId && !this.reference;
    }
  },
  watch: {
    imageSets: {
      deep: true,
      handler() {
        this.destroyScene();
        this.setupScene();
      }
    },
    sceneState: {
      deep: true,
      handler(value) {
        this.$emit('SceneStateLoading', value.loading);
      }
    },
    skus: {
      deep: true,
      async handler() {
        bus.updatedSkuList = this.skus;
        bus.$emit(EVENTS.RE_RENDER_CROP);
      }
    },
    async isFullScreen() {
      await this.destroyScene();

      if (this.isFullScreen) {
        this.sceneHeightGap = 0;
        document.getElementsByClassName('report-inspector-scene')[0].style.height = this.isMobile ? 'calc(100vh - 173px)' : 'calc(100vh - 60px)';
      } else {
        if (!this.isMobile) {
          this.sceneHeightGap = 0;
        } else {
          this.sceneHeightGap = (this.isSkuListActive || this.recognitionResults) ? 330 : 304;
        }
        document.getElementsByClassName('report-inspector-scene')[0].style.height = 'calc(100vh - 264px)';
      }

      await this.setupScene();
    },
    async recognitionResults() {
      bus.$emit(EVENTS.RE_RENDER_CROP);
    },
    async selectedImageNumber() {
      this.destroyScene();
      this.setupScene();
    }
  },
  async mounted() {
    this.bus.projectId = this.Project.active.data.id;
    bus.updatedSkuList = this.skus;

    if (!this.isMobile) {
      this.sceneHeightGap = 0;
    } else {
      this.sceneHeightGap = (this.isSkuListActive || this.recognitionResults) ? 330 : 304;
    }
    if (this.imageSets.length > 0) {
      await this.setupScene();
    }

    this.popperOptions = { boundariesElement: this.$el };
    this.$refs.toolTipElement.updatePopper();
  },
  beforeDestroy() {
    this.destroyScene();
  },
  methods: {
    async setupScene() {
      this.sceneState.loading = true;
      this.sceneState.error = false;

      bus.$on(EVENTS.SHOW_TOOL_TIP, c => this.showToolTip(c));
      bus.$on(EVENTS.DESTROY_TOOLTIP, c => this.destroyToolTip(c));
      bus.$on(EVENTS.RE_RENDER_CROP, () => this.updateToolTip());

      try {
        const { width, height } = this.$el.getBoundingClientRect();

        this.sceneCanvas = new ReportInspectorScene(
          { width, height: this.isMobile ? window.innerHeight - this.sceneHeightGap : height },
          this.mosaic,
          this.imageSets,
          this.imageResults,
          this.report,
          this.skus,
          this.selectedImageNumber,
          this.isReversed,
          {
            bgAlpha: this.bgAlpha,
            reference: this.reference,
            sceneZoomScaleValue: this.sceneZoomScaleValue,
            referenceImages: this.reference ? this.reportDataImages : null,
            imageSetId: this.reference ? this.referenceImageSetId : this.imageSetId,
            sceneTitle: this.sceneTitle
          }
        );

        await this.sceneCanvas.setupMaterials();

        this.$el.append(this.sceneCanvas.view);

        this.sceneState.error = false;
      } catch (error) {
        this.sceneState.error = true;
        console.warn(error);
      }

      this.sceneState.loading = false;
    },
    showToolTip(item) {
      if (item.crop.crop.class.id && (item.reference === this.reference) && !(this.isSkuListActive && this.isMobile)) {
        this.crop = {
          ...this.skus.find(sku => sku.id.toString() === item.crop.crop.class.id.toString()),
          ...item.crop,
          ...this.nonSkuList.find(sku => sku.id.toString() === item.crop.crop.class.id.toString())
        };
        this.addCropState();
        if (this.timeout) clearTimeout(this.timeout);

        this.timeout = setTimeout(() => {
          this.show_tooltip = true;
          this.$refs.toolTipElement.updatePopper();
          this.$refs.toolTipElement.show();
        }, 300);
      }
    },
    destroyToolTip() {
      setTimeout(() => {
        clearTimeout(this.timeout);
        this.show_tooltip = false;
        this.cropState = null;
        if (this.$refs.toolTipElement) { this.$refs.toolTipElement.hide(); }
      }, 300);
    },
    updateToolTip() {
      this.$nextTick().then(() => {
        this.$refs.toolTipElement.updatePopper();
      });
    },
    destroyScene() {
      const { sceneCanvas } = this;

      if (sceneCanvas) {
        sceneCanvas.renderer.gl.getExtension('WEBGL_lose_context').loseContext();
        sceneCanvas.destroy(true);
        sceneCanvas.viewport.destroy(true);

        this.sceneCanvas = null;
      }

      bus.viewport = null;
      bus.selected_crop = null;
      bus.$off();

      this.show_tooltip = false;
      if (this.$refs.toolTipElement) { this.$refs.toolTipElement.hide(); }
      PIXI.utils.destroyTextureCache();
      PIXI.utils.clearTextureCache();
    },
    handleClickOutside() {
      this.destroyToolTip();
    },
    handleFitToScreen() {
      bus.$emit(EVENTS.FIT_TO_SCREEN);
    },
    addCropState() {
      const { crop } = this.crop;

      if (crop.legendState) {
        const selectedLegend = bus.selectedLegends.find(legend => legend.id === crop.legendState);
        this.cropState = {
          label: crop.legendState,
          color: selectedLegend.color
        };
      } else if (crop.is_excessRealogram) {
        this.cropState = {
          label: 'excess',
          color: '#76ff5f'
        };
      } else if (crop.is_misplacedRealogram) {
        this.cropState = {
          label: 'misplaced',
          color: '#6efff1'
        };
      } else if (crop.is_missingRealogram) {
        this.cropState = {
          label: 'missing',
          color: '#f9cb49'
        };
      } else if (crop.is_out_of_stockRealogram) {
        this.cropState = {
          label: 'out_of_stock',
          color: '#ff4444'
        };
      }
    }
  }
};
</script>

<style lang="scss">
.report-inspector-scene {
  position: relative;
  flex: 1;
  height: calc(100vh - 329px);
  overflow: hidden;

  &__tooltip {
    z-index: 4000 !important;
    border: 0 !important;
    box-shadow: 0 0 8px 0 rgba($black, 0.1);

    &-content {
      display: flex;
      align-items: center;

      &.is-unrecognized {
        flex-direction: column;

        .el-button {
          width: 100%;
        }
      }

      &.is-meta {
        flex-direction: column;

        .el-button {
          width: 100%;
        }
      }

      > i {
        font-size: 40px;
        opacity: 0.3;
      }

      .selected-preview-box {
        .el-button {
          width: 100%;
        }
      }

      .report-inspector {
        width: 100%;
        min-width: 300px;
        min-height: 120px;

        &__content {
          display: flex;
          align-items: center;
          font-size: 13px;

          &-image {
            width: 100%;
            height: 100px;
            margin: 10px auto;
            background-repeat: no-repeat;
            background-position: center;
            background-size: contain;
          }

          .detail {
            display: flex;
            justify-content: space-between;
            font-size: 11px;
            line-height: 15px;
          }
        }

        .crop-legend-state {
          font-size: 18px;
          font-weight: 700;
          line-height: 25px;
        }
      }
    }
  }

  .fit-to-screen-button {
    position: absolute;
    right: 20px;
    bottom: 20px;
    padding: 2px 6px;
    font-size: 22px;
    color: black;
    cursor: pointer;
    background-color: #6efff1;
    border-radius: 50px;
  }
}
</style>
